import { getScopedDomain } from '../lib/tenantScope';
// Push Devices — admin view of registered mobile/web push tokens. Lets admins
// see what devices are bound to which extension and disable misbehaving
// tokens manually.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Smartphone, Search, Power } from 'lucide-react';
import { api } from '../lib/api';

const TYPE_BADGES: Record<string, string> = {
  apns:    'bg-blue-500/20 text-blue-400',
  fcm:     'bg-orange-500/20 text-orange-400',
  webpush: 'bg-purple-500/20 text-purple-400',
  wns:     'bg-cyan-500/20 text-cyan-400',
};

export default function PushDevices() {
  const me = api.me();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: list = [] } = useQuery({
    queryKey: ['push-regs', getScopedDomain(me?.domain_uuid)],
    queryFn: () => getScopedDomain(me?.domain_uuid) ? api.pushRegistrations.list({ domain_uuid: getScopedDomain(me?.domain_uuid) }) : Promise.resolve([]),
    enabled: !!getScopedDomain(me?.domain_uuid),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.pushRegistrations.update(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['push-regs'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.pushRegistrations.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['push-regs'] }),
  });

  const filtered = list.filter((r: any) =>
    !search ||
    r.extension?.includes(search) ||
    r.model_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.custom_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.destination_address?.includes(search));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Smartphone /> Push Devices</h1>
        <span className="text-xs text-textSecondary">{list.length} registered</span>
      </div>

      <div className="card p-3 flex items-center gap-2">
        <Search size={16} className="text-textSecondary" />
        <input className="input flex-1" placeholder="Filter by extension, model, custom name…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr>
            <th></th>
            <th>Extension</th>
            <th>Type</th>
            <th>Device</th>
            <th>Token (truncated)</th>
            <th>Last activity</th>
            <th></th>
          </tr></thead>
          <tbody>
            {filtered.map((r: any) => (
              <tr key={r.push_uuid} className="hover:bg-elevated">
                <td>
                  <span className={`inline-block w-2 h-2 rounded-full ${r.enabled ? 'bg-success' : 'bg-danger'}`} title={r.enabled ? 'enabled' : 'disabled'} />
                </td>
                <td className="font-mono">{r.extension || <span className="text-textSecondary">unbound</span>}</td>
                <td><span className={`badge ${TYPE_BADGES[r.delivery_type] || ''}`}>{r.delivery_type}</span></td>
                <td>
                  <div>{r.custom_name || r.model_name || '—'}</div>
                  {r.custom_name && r.model_name && <div className="text-xs text-textSecondary">{r.model_name}</div>}
                </td>
                <td className="font-mono text-xs text-textSecondary">{(r.destination_address || '').slice(0, 24)}…</td>
                <td className="text-xs">{r.last_activity ? new Date(r.last_activity).toLocaleString() : '—'}</td>
                <td className="space-x-1 whitespace-nowrap">
                  <button className="btn-ghost p-1" title={r.enabled ? 'Disable' : 'Enable'} onClick={() => toggle.mutate({ id: r.push_uuid, enabled: !r.enabled })}>
                    <Power size={14} className={r.enabled ? 'text-success' : 'text-danger'} />
                  </button>
                  <button className="btn-ghost p-1 text-danger" onClick={() => confirm('Remove?') && remove.mutate(r.push_uuid)}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-textSecondary">No devices registered.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card p-3 text-sm text-textSecondary">
        <strong>Tip:</strong> tokens disable themselves automatically on permanent
        APNS/FCM errors (BadDeviceToken, NotRegistered, 410). The toggle here is a
        manual override.
      </div>
    </div>
  );
}
